import { Component, Match, Switch } from 'solid-js';
import {
  SiFacebook,
  SiInstagram,
  SiReddit,
  SiWhatsapp,
  SiTiktok,
  SiTelegram,
  SiTwitter,
  SiLinkedin,
  SiPinterest,
  SiDiscord,
  SiYoutube,
  SiSnapchat,
} from 'solid-icons/si';
import { TbWorldWww, TbWorld } from 'solid-icons/tb';
import ThreadsIcon from './ThreadsIcon';

interface PlatformIconProps {
  platform?: string | null;
  size?: number;
  class?: string;
  onClick?: () => void;
  showLabel?: boolean;
}

/**
 * Platform Icon Component - Unified Icon Component for All Platforms
 *
 * Displays appropriate icons for:
 * - Social Media: Facebook, Instagram, TikTok, Twitter, Telegram, WhatsApp, LinkedIn, Reddit, Pinterest, Discord, YouTube, Snapchat, Threads
 * - Browsers: Chrome, Firefox, Safari, Edge
 * - Traffic Types: Direct, Organic, Web, Website
 * - Messaging: WhatsApp, Telegram, Signal, etc.
 *
 * Supports both full names and short codes:
 * - 'Facebook' or 'fb' → Facebook icon
 * - 'WhatsApp' or 'wa' → WhatsApp icon
 * - 'TikTok' or 'tt' → TikTok icon
 * - etc.
 */
const PlatformIcon: Component<PlatformIconProps> = (props) => {
  const size = () => props.size || 16;

  /**
   * Get readable platform name from code or full name
   */
  const getPlatformName = (platform?: string | null): string => {
    if (!platform) return 'Web';
    const platformLower = String(platform).toLowerCase().trim();
    const nameMap: Record<string, string> = {
      // Social Media
      'fb': 'Facebook',
      'facebook': 'Facebook',
      'ig': 'Instagram',
      'instagram': 'Instagram',
      'twitter': 'Twitter',
      'x': 'X (Twitter)',
      'telegram': 'Telegram',
      'tg': 'Telegram',
      'whatsapp': 'WhatsApp',
      'wa': 'WhatsApp',
      'tiktok': 'TikTok',
      'tt': 'TikTok',
      'reddit': 'Reddit',
      'threads': 'Threads',
      'linkedin': 'LinkedIn',
      'pinterest': 'Pinterest',
      'discord': 'Discord',
      'youtube': 'YouTube',
      'snapchat': 'Snapchat',
      // Browsers
      'chrome': 'Chrome',
      'firefox': 'Firefox',
      'safari': 'Safari',
      'edge': 'Edge',
      // Traffic Types
      'web': 'Web',
      'website': 'Website',
      'direct': 'Direct',
      'organic': 'Organic',
    };
    return nameMap[platformLower] || 'Web';
  };

  /**
   * Normalize platform code/name to lowercase for matching
   */
  const getPlatformType = (platform?: string | null): string => {
    if (!platform) return 'web';
    return String(platform).toLowerCase().trim();
  };

  /**
   * Render appropriate icon based on platform
   */
  const renderIcon = () => {
    const platformType = getPlatformType(props.platform);
    const platformName = getPlatformName(props.platform);
    const iconSize = size();
    const commonProps = {
      size: iconSize,
      class: `flex-shrink-0 cursor-pointer ${props.class}`,
      title: platformName,
      onClick: props.onClick,
    };

    return (
      <Switch fallback={<TbWorldWww {...commonProps} />}>
        {/* Social Media */}
        <Match when={['fb', 'facebook'].includes(platformType)}>
          <SiFacebook {...commonProps} />
        </Match>
        <Match when={['ig', 'instagram'].includes(platformType)}>
          <SiInstagram {...commonProps} />
        </Match>
        <Match when={['twitter', 'x'].includes(platformType)}>
          <SiTwitter {...commonProps} />
        </Match>
        <Match when={['telegram', 'tg'].includes(platformType)}>
          <SiTelegram {...commonProps} />
        </Match>
        <Match when={['whatsapp', 'wa'].includes(platformType)}>
          <SiWhatsapp {...commonProps} />
        </Match>
        <Match when={['tiktok', 'tt'].includes(platformType)}>
          <SiTiktok {...commonProps} />
        </Match>
        <Match when={['reddit'].includes(platformType)}>
          <SiReddit {...commonProps} />
        </Match>
        <Match when={['threads'].includes(platformType)}>
          <ThreadsIcon
            size={iconSize}
            class={`flex-shrink-0 cursor-pointer ${props.class}`}
            title={platformName}
            onClick={props.onClick}
          />
        </Match>
        <Match when={['linkedin'].includes(platformType)}>
          <SiLinkedin {...commonProps} />
        </Match>
        <Match when={['pinterest'].includes(platformType)}>
          <SiPinterest {...commonProps} />
        </Match>
        <Match when={['discord'].includes(platformType)}>
          <SiDiscord {...commonProps} />
        </Match>
        <Match when={['youtube'].includes(platformType)}>
          <SiYoutube {...commonProps} />
        </Match>
        <Match when={['snapchat'].includes(platformType)}>
          <SiSnapchat {...commonProps} />
        </Match>

        {/* Browsers */}
        <Match when={['chrome', 'firefox', 'safari', 'edge'].includes(platformType)}>
          <TbWorldWww {...commonProps} />
        </Match>

        {/* Traffic Types */}
        <Match when={['direct', 'organic', 'web', 'website'].includes(platformType)}>
          <TbWorld {...commonProps} />
        </Match>
      </Switch>
    );
  };

  return renderIcon();
};

/**
 * Get platform color with dark mode support
 * Returns Tailwind color classes for consistent styling
 */
export const getPlatformColor = (platform?: string | null): string => {
  if (!platform) return 'text-gray-600 dark:text-gray-400';

  const platformLower = String(platform).toLowerCase().trim();

  const colorMap: Record<string, string> = {
    // Social Media
    'fb': 'text-blue-600 dark:text-blue-400',
    'facebook': 'text-blue-600 dark:text-blue-400',
    'ig': 'text-pink-600 dark:text-pink-400',
    'instagram': 'text-pink-600 dark:text-pink-400',
    'twitter': 'text-sky-500 dark:text-sky-400',
    'x': 'text-black dark:text-white',
    'telegram': 'text-blue-500 dark:text-blue-400',
    'tg': 'text-blue-500 dark:text-blue-400',
    'whatsapp': 'text-green-600 dark:text-green-400',
    'wa': 'text-green-600 dark:text-green-400',
    'tiktok': 'text-black dark:text-white',
    'tt': 'text-black dark:text-white',
    'reddit': 'text-orange-600 dark:text-orange-400',
    'threads': 'text-black dark:text-white',
    'linkedin': 'text-blue-700 dark:text-blue-400',
    'pinterest': 'text-red-600 dark:text-red-400',
    'discord': 'text-indigo-600 dark:text-indigo-400',
    'youtube': 'text-red-600 dark:text-red-400',
    'snapchat': 'text-yellow-400 dark:text-yellow-300',
    // Browsers
    'chrome': 'text-gray-600 dark:text-gray-400',
    'firefox': 'text-gray-600 dark:text-gray-400',
    'safari': 'text-gray-600 dark:text-gray-400',
    'edge': 'text-gray-600 dark:text-gray-400',
    // Traffic Types
    'web': 'text-gray-600 dark:text-gray-400',
    'website': 'text-gray-600 dark:text-gray-400',
    'direct': 'text-gray-600 dark:text-gray-400',
    'organic': 'text-green-600 dark:text-green-400',
  };

  return colorMap[platformLower] || 'text-gray-600 dark:text-gray-400';
};

/**
 * Backward compatibility export (old name)
 * @deprecated Use getPlatformColor instead
 */
export const getSocialColor = getPlatformColor;

export default PlatformIcon;
